'use client';

/**
 * Shared analytics primitives (spec §13) — quiet, editorial, low-ink.
 *
 * Lantern-styled metric cards, bar meters, and formatters reused across the
 * campaign analytics dashboard, the per-node tree overlay, the job-responsiveness
 * view, and the Settings account analytics. No chart library — just CSS/SVG bars
 * so the surface stays calm and the bundle stays small. Coral is reserved for the
 * single goal-led headline metric; everything else uses warm neutrals.
 */

import type { ReactNode } from 'react';

/* ─── formatters ─── */

export const pct = (n: number) => {
    if (!isFinite(n) || n <= 0) return '0%';
    if (n < 0.01) return `${(n * 100).toFixed(1)}%`;
    return `${Math.round(n * 100)}%`;
};

export const num = (n: number) => (n || 0).toLocaleString();

/** Human-friendly duration from milliseconds: "—" / "45m" / "2h 10m" / "3d 4h". */
export function dur(ms: number): string {
    if (!ms || ms <= 0) return '—';
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        const rem = mins % 60;
        return rem ? `${hours}h ${rem}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    return remH ? `${days}d ${remH}h` : `${days}d`;
}

/** Relative "time ago" for last-activity timestamps. */
export function timeAgo(iso: string | null): string {
    if (!iso) return 'never';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return 'just now';
    if (ms < 60000) return 'just now';
    return `${dur(ms)} ago`;
}

/* ─── metric card ─── */

export function MetricCard({
    label,
    value,
    hint,
    accent,
}: {
    label: string;
    value: string;
    hint?: string;
    accent?: boolean;
}) {
    return (
        <div
            className="rounded-[var(--radius-lg)] px-3.5 py-3"
            style={{
                background: 'var(--surface-primary)',
                border: `1px solid ${accent ? 'var(--border-accent)' : 'var(--border-default)'}`,
            }}
        >
            <div className="text-[11px] tracking-[0.04em] uppercase" style={{ color: 'var(--text-tertiary)' }}>
                {label}
            </div>
            <div className="mt-1 flex items-baseline gap-1.5">
                <span
                    className="text-[20px] leading-none font-semibold tabular-nums"
                    style={{ color: accent ? 'var(--accent-emphasis)' : 'var(--text-primary)' }}
                >
                    {value}
                </span>
                {hint && (
                    <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                        {hint}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ─── horizontal bar meter ─── */

export function BarMeter({
    value,
    max = 1,
    tone = 'neutral',
}: {
    /** 0..max. When max is 1 this is a fraction (a rate). */
    value: number;
    max?: number;
    tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
}) {
    const fillPct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
    const fill = {
        neutral: 'var(--text-tertiary)',
        accent: 'var(--accent)',
        success: 'var(--fg-success)',
        warning: 'var(--fg-warning)',
        danger: 'var(--fg-danger)',
    }[tone];
    return (
        <div
            className="h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: 'var(--surface-tertiary)' }}
        >
            <div className="h-full rounded-full transition-all" style={{ width: `${fillPct}%`, background: fill }} />
        </div>
    );
}

/** A labelled rate row: name · value, with a meter underneath. */
export function RateRow({
    label,
    value,
    display,
    tone = 'neutral',
    sub,
}: {
    label: string;
    value: number;
    display: string;
    tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
    sub?: string;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
                    {label}
                </span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                    {display}
                    {sub && (
                        <span className="ml-1 text-[11px] font-normal" style={{ color: 'var(--text-tertiary)' }}>
                            {sub}
                        </span>
                    )}
                </span>
            </div>
            <BarMeter value={value} tone={tone} />
        </div>
    );
}

/* ─── quiet empty state ─── */

export function AnalyticsEmpty({ icon, children }: { icon: ReactNode; children: ReactNode }) {
    return (
        <div
            className="flex flex-col items-center gap-2 rounded-[var(--radius-lg)] px-6 py-10 text-center"
            style={{ background: 'var(--surface-secondary)', border: '1px dashed var(--border-emphasis)' }}
        >
            <span style={{ color: 'var(--text-tertiary)' }}>{icon}</span>
            <p className="max-w-xs text-[12.5px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
                {children}
            </p>
        </div>
    );
}

/* ─── tab strip ─── */

export function AnalyticsTabs<T extends string>({
    tabs,
    active,
    onChange,
}: {
    tabs: { id: T; label: string }[];
    active: T;
    onChange: (id: T) => void;
}) {
    return (
        <div
            className="flex items-center gap-1 overflow-x-auto v2-scrollbar-hide"
            style={{ borderBottom: '1px solid var(--border-muted)' }}
        >
            {tabs.map(({ id, label }) => {
                const on = id === active;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onChange(id)}
                        className="relative h-8 px-2.5 text-[12.5px] font-medium whitespace-nowrap transition-colors"
                        style={{ color: on ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
                    >
                        {label}
                        {on && (
                            <span
                                className="absolute right-0 -bottom-px left-0 h-0.5 rounded-full"
                                style={{ background: 'var(--accent)' }}
                            />
                        )}
                    </button>
                );
            })}
        </div>
    );
}
